import { GET_EHR_APPOINTMENTS } from '@/hooks/query/integrations/useQueryEHRAppointments';
import { updateSessionById } from '@/pages/api/scribeAPI';
import { toast } from '@heidi/design-system/toast';
import { type SearchAppointmentResponse } from '@heidi/integrations/ehr/contracts';
import { useMutateUpdateSessionEHRAppointmentId as useMutateUpdateSessionEHRAppointmentIdCore } from '@heidi/integrations/ehr/data';
import {
  GET_SESSION_BY_ID,
  type QuerySessionResponse,
  type SessionResponse,
} from '@heidi/session/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { format } from 'date-fns';
import { get } from 'lodash-es';

export const useMutateUpdateSessionEHRAppointmentId = (date?: Date) => {
  const queryClient = useQueryClient();

  const dateInput = date ? format(date, 'yyyy-MM-dd') : undefined;

  return useMutateUpdateSessionEHRAppointmentIdCore<
    Awaited<ReturnType<typeof updateSessionById>>
  >({
    updateSession: updateSessionById,
    onSuccess: (res) => {
      if (!res?.data) {
        return;
      }
      const data = res.data;
      const {
        session_id: sessionId,
        ehr_appt_id,
        created_time_local,
        ehr_provider: ehrProviderFromData,
      } = data;
      queryClient.setQueryData<QuerySessionResponse>(
        [GET_SESSION_BY_ID, { sessionId }],
        (oldData) => {
          if (!oldData) {
            return undefined;
          }
          const newData: SessionResponse = {
            ...oldData,
            ehr_appt_id,
            created_time_local,
            ehr_provider: oldData.patient_profile?.ehr ?? ehrProviderFromData,
          };
          return newData;
        }
      );
    },
    onMutate: async ({ EHRApptId, sessionId }) => {
      if (!date) {
        return;
      }
      await queryClient.cancelQueries({
        queryKey: [GET_EHR_APPOINTMENTS, { date: dateInput }],
      });
      const previousAppointments = queryClient.getQueryData([
        GET_EHR_APPOINTMENTS,
        { date: dateInput },
      ]);

      queryClient.setQueryData<SearchAppointmentResponse>(
        [GET_EHR_APPOINTMENTS, { date: dateInput }],
        (oldData) => {
          if (!oldData?.appointments) {
            return oldData;
          }

          const newAppointments = oldData.appointments.map((appointment) => {
            if (appointment.record_id === EHRApptId) {
              return {
                ...appointment,
                session_id: sessionId,
              };
            }
            return appointment;
          });

          return { appointments: newAppointments };
        }
      );

      return { previousAppointments };
    },

    onError: (error) => {
      void queryClient.invalidateQueries({
        queryKey: [GET_EHR_APPOINTMENTS, { date: dateInput }],
      });

      if (error instanceof AxiosError) {
        toast({
          tone: 'destructive',
          title: 'Failed to update session',
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          description: get(error.response, 'data.detail.message', error.message),
        });
      }
    },
  });
};
